import { useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ApiError } from "../api/client";
import {
  useApprovalSummary,
  useApprove,
  useBulkApprove,
  useBulkReject,
  useDocument,
  useIndexPending,
  usePendingApprovals,
  useReject,
  useReopen,
  useReviewHistory,
} from "../api/queries";
import type { ApprovalDocument, ApprovalStatus, ApprovalSummary } from "../api/types";
import { useAuth } from "../api/useAuth";
import { JobNotice } from "../components/JobNotice";
import { Panel } from "../components/Panel";
import { PageNav } from "../components/PageNav";
import { Button, Empty, ErrorBanner, Loading, PageHeader, RelativeTime, StatusBadge } from "../components/ui";
import { bytes, titleCase } from "../lib/format";

const PAGE_SIZE = 50;

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

// Summary tile from the Content-review design: centred, brand-coloured count.
function StatCard({ label, value, hint }: { label: string; value: number | undefined; hint: string }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-5 text-center shadow-sm">
      <div className="text-sm font-semibold text-brand">{label}</div>
      <div className="mt-1 text-3xl font-semibold tabular-nums text-brand">{value === undefined ? "—" : pad2(value)}</div>
      <div className="mt-1 text-xs text-gray-500">{hint}</div>
    </div>
  );
}

// Row-card tables: brand header bar over separated rounded white rows.
const TABLE = "w-full border-separate border-spacing-x-0 border-spacing-y-2";
const HEAD_CELL = "bg-brand px-4 py-3 text-left text-xs font-semibold whitespace-nowrap text-white";
const BODY_CELL = "border-y border-gray-100 px-4 py-3 align-middle";

function headCell(pos: "first" | "mid" | "last") {
  return [HEAD_CELL, pos === "first" && "rounded-l-lg", pos === "last" && "rounded-r-lg"].filter(Boolean).join(" ");
}

function bodyCell(pos: "first" | "mid" | "last", selected = false) {
  return [
    BODY_CELL,
    pos === "first" && "rounded-l-lg border-l",
    pos === "last" && "rounded-r-lg border-r",
    selected ? "bg-brand-light" : "bg-white group-hover:bg-gray-50",
  ].filter(Boolean).join(" ");
}

type Tab = "pending" | "history";
// How the pending queue is shown when no course is picked: one row per course
// (approve whole courses at once) or every file across courses.
type PendingView = "courses" | "files";

const HISTORY_FILTERS: { id: string; label: string; statuses: ApprovalStatus[] }[] = [
  { id: "decided", label: "Approved + rejected", statuses: ["approved", "rejected"] },
  { id: "approved", label: "Approved", statuses: ["approved"] },
  { id: "rejected", label: "Rejected", statuses: ["rejected"] },
  { id: "retired", label: "Retired", statuses: ["retired"] },
];

// ?tab=pending|history, ?view=courses|files, ?course=<moodle id>, ?module=<cmid>, ?status=<history filter>
function useFilters() {
  const [params, setParams] = useSearchParams();
  const num = (key: string) => (params.get(key) ? Number(params.get(key)) : undefined);
  const update = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    setParams(next, { replace: true });
  };
  return {
    tab: (params.get("tab") === "history" ? "history" : "pending") as Tab,
    view: (params.get("view") === "files" ? "files" : "courses") as PendingView,
    courseMoodleId: num("course"),
    moduleCmid: num("module"),
    historyFilter: HISTORY_FILTERS.find((f) => f.id === params.get("status")) ?? HISTORY_FILTERS[0],
    update,
  };
}

export function ApprovalsPage() {
  const { tab, view, courseMoodleId, moduleCmid, update } = useFilters();
  const byCourse = tab === "pending" && view === "courses" && courseMoodleId === undefined && moduleCmid === undefined;
  const summary = useApprovalSummary();
  const courseCounts = summary.data?.courses.find((c) => c.course_moodle_id === courseMoodleId);
  const counts = courseMoodleId !== undefined ? courseCounts : summary.data?.totals;
  const historyCount = counts ? counts.approved + counts.rejected + counts.retired : undefined;

  return (
    <div className="p-6">
      <PageHeader
        title="Content review"
        subtitle="New content extracted from Moodle waits here. Only approved documents are indexed for the tutor."
      />
      <JobNotice kind="index" />

      <ErrorBanner error={summary.error} />
      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard label="Pending" value={counts?.pending} hint={courseMoodleId !== undefined ? "in this course" : "across all courses"} />
        <StatCard label="Approved" value={counts?.approved} hint="ready to index" />
        <StatCard label="Rejected" value={counts?.rejected} hint="excluded from tutor" />
        <StatCard label="Retired" value={counts?.retired} hint="removed from Moodle" />
      </div>

      <div className="mb-4 flex flex-wrap gap-2" role="tablist" aria-label="Review status">
        {(["pending", "history"] as const).map((t) => {
          const active = tab === t;
          const count = t === "pending" ? counts?.pending : historyCount;
          return (
            <button
              key={t}
              role="tab"
              aria-selected={active}
              onClick={() => update({ tab: t === "pending" ? undefined : t })}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium transition ${
                active ? "bg-brand text-white shadow-sm" : "border border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
              }`}
            >
              {t === "pending" ? "Pending" : "History"}
              {count !== undefined && (
                <span className={`rounded-full px-2 py-0.5 text-xs tabular-nums ${active ? "bg-white/20 text-white" : "bg-gray-100 text-gray-500"}`}>
                  {pad2(count)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select
          aria-label="Course"
          value={courseMoodleId ?? ""}
          onChange={(e) => update({ course: e.target.value || undefined, module: undefined })}
          className="min-w-64 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-brand focus:outline-none"
        >
          <option value="">All courses</option>
          {(summary.data?.courses ?? [])
            .slice()
            .sort((a, b) => b.pending - a.pending || a.course_fullname.localeCompare(b.course_fullname))
            .map((c) => (
              <option key={c.course_moodle_id} value={c.course_moodle_id}>
                {c.course_fullname} ({c.pending} pending)
              </option>
            ))}
        </select>
        {moduleCmid !== undefined && (
          <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-700">
            Module cmid {moduleCmid}
            <button onClick={() => update({ module: undefined })} aria-label="Clear module filter" className="ml-1 text-gray-500 hover:text-gray-900">✕</button>
          </span>
        )}
        {courseMoodleId !== undefined && (
          <Link to={`/courses/${courseMoodleId}`} className="text-sm font-medium text-brand hover:underline">Open course</Link>
        )}

        {tab === "pending" && courseMoodleId === undefined && moduleCmid === undefined && (
          <div className="ml-auto inline-flex rounded-full border border-gray-200 bg-white p-0.5 text-sm" role="tablist" aria-label="Group pending content">
            {(["courses", "files"] as const).map((v) => (
              <button
                key={v}
                role="tab"
                aria-selected={view === v}
                onClick={() => update({ view: v === "courses" ? undefined : v })}
                className={`rounded-full px-4 py-1.5 font-medium transition ${view === v ? "bg-brand text-white" : "text-gray-600 hover:text-gray-900"}`}
              >
                {v === "courses" ? "By course" : "All files"}
              </button>
            ))}
          </div>
        )}
      </div>

      {byCourse
        ? <CourseQueue summary={summary.data} loading={summary.isLoading} onOpen={(id) => update({ course: String(id), view: undefined })} />
        : tab === "pending"
        ? <PendingTab key={`${courseMoodleId}-${moduleCmid}`} courseMoodleId={courseMoodleId} moduleCmid={moduleCmid} />
        : <HistoryTab key={`${courseMoodleId}-${moduleCmid}`} courseMoodleId={courseMoodleId} moduleCmid={moduleCmid} />}
    </div>
  );
}

// --- pending, by course ---------------------------------------------------------------

// One row per course with pending content. Whole courses can be approved or
// rejected in one call; "Review files" drops into the file list for that course,
// where individual files can still be picked.
function CourseQueue({ summary, loading, onOpen }: {
  summary?: ApprovalSummary;
  loading: boolean;
  onOpen: (courseMoodleId: number) => void;
}) {
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [justApproved, setJustApproved] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const bulkApprove = useBulkApprove();
  const bulkReject = useBulkReject();
  const indexPending = useIndexPending();
  const busy = bulkApprove.isPending || bulkReject.isPending;

  const courses = (summary?.courses ?? [])
    .filter((c) => c.pending > 0)
    .sort((a, b) => b.pending - a.pending || a.course_fullname.localeCompare(b.course_fullname));
  // Ignore selections for courses that have since emptied.
  const picked = courses.filter((c) => selected.has(c.course_moodle_id));
  const pickedDocs = picked.reduce((sum, c) => sum + c.pending, 0);
  const allSelected = courses.length > 0 && picked.length === courses.length;
  const docs = (n: number) => `${n.toLocaleString()} pending document${n === 1 ? "" : "s"}`;
  const courseWord = (n: number) => `${n} course${n === 1 ? "" : "s"}`;

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function decide(decision: "approve" | "reject", ids: number[], label: string, count: number) {
    const question = decision === "approve"
      ? `Approve ${docs(count)} in ${label} without reviewing them individually?`
      : `Reject ${docs(count)} in ${label}?`;
    if (!confirm(question)) return;
    const mutation = decision === "approve" ? bulkApprove : bulkReject;
    mutation.mutate({ courseMoodleIds: ids }, {
      onSuccess: (r) => {
        setSelected(new Set());
        if (decision === "approve") setJustApproved((n) => n + r.count);
        setNotice(`${r.count.toLocaleString()} ${decision === "approve" ? "approved" : "rejected"} in ${label}.`);
      },
    });
  }

  return (
    <>
      <ErrorBanner error={bulkApprove.error || bulkReject.error || indexPending.error} />
      {justApproved > 0 && !indexPending.data && (
        <div className="mb-4 flex items-center justify-between gap-4 rounded border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">
          <span>{justApproved.toLocaleString()} document{justApproved === 1 ? "" : "s"} approved. Index them so the tutor can use them.</span>
          <Button variant="brand" loading={indexPending.isPending} onClick={() => indexPending.mutate()}>Index now</Button>
        </div>
      )}
      {notice && !justApproved && <p className="mb-4 text-sm text-gray-700">{notice}</p>}

      <div className="mb-4 flex flex-wrap justify-end gap-2">
        <Button
          variant="danger"
          disabled={!picked.length || busy}
          loading={bulkReject.isPending}
          onClick={() => decide("reject", picked.map((c) => c.course_moodle_id), courseWord(picked.length), pickedDocs)}
        >
          Reject selected courses ({picked.length})
        </Button>
        <Button
          variant="brand"
          disabled={!picked.length || busy}
          loading={bulkApprove.isPending}
          onClick={() => decide("approve", picked.map((c) => c.course_moodle_id), courseWord(picked.length), pickedDocs)}
        >
          Approve selected courses ({picked.length}{picked.length ? ` · ${pickedDocs.toLocaleString()} docs` : ""})
        </Button>
      </div>

      <Panel>
        {loading ? <Loading /> : !courses.length ? (
          <Empty>Nothing waiting for approval. Run an extraction after syncing to bring in new content.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={`${headCell("first")} w-8`}>
                    <input
                      type="checkbox"
                      aria-label="Select all courses"
                      checked={allSelected}
                      onChange={() => setSelected(allSelected ? new Set() : new Set(courses.map((c) => c.course_moodle_id)))}
                    />
                  </th>
                  <th className={headCell("mid")}>No.</th>
                  <th className={headCell("mid")}>Course</th>
                  <th className={headCell("mid")}>Pending</th>
                  <th className={headCell("mid")}>Approved</th>
                  <th className={headCell("mid")}>Rejected</th>
                  <th className={headCell("last")} />
                </tr>
              </thead>
              <tbody>
                {courses.map((c, i) => {
                  const sel = selected.has(c.course_moodle_id);
                  return (
                    <tr key={c.course_moodle_id} className="group">
                      <td className={`${bodyCell("first", sel)} w-8`}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${c.course_fullname}`}
                          checked={sel}
                          onChange={() => toggle(c.course_moodle_id)}
                        />
                      </td>
                      <td className={`${bodyCell("mid", sel)} tabular-nums text-gray-500`}>{pad2(i + 1)}</td>
                      <td className={bodyCell("mid", sel)}>
                        <button onClick={() => onOpen(c.course_moodle_id)} className="text-left font-medium text-gray-900 hover:text-brand hover:underline">{c.course_fullname}</button>
                        <div className="text-xs text-gray-500">{c.course_shortname} · id {c.course_moodle_id}</div>
                      </td>
                      <td className={`${bodyCell("mid", sel)} font-medium tabular-nums text-amber-700`}>{c.pending.toLocaleString()}</td>
                      <td className={`${bodyCell("mid", sel)} tabular-nums`}>{c.approved.toLocaleString()}</td>
                      <td className={`${bodyCell("mid", sel)} tabular-nums text-gray-500`}>{c.rejected.toLocaleString()}</td>
                      <td className={`${bodyCell("last", sel)} whitespace-nowrap text-right`}>
                        <span className="inline-flex items-center gap-2">
                          <Button onClick={() => onOpen(c.course_moodle_id)}>Review files</Button>
                          <Button
                            variant="brand"
                            disabled={busy}
                            loading={bulkApprove.isPending && bulkApprove.variables?.courseMoodleIds?.length === 1 && bulkApprove.variables.courseMoodleIds[0] === c.course_moodle_id}
                            onClick={() => decide("approve", [c.course_moodle_id], `“${c.course_fullname}”`, c.pending)}
                          >
                            Approve course
                          </Button>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

// --- pending, by file ---------------------------------------------------------------

function PendingTab({ courseMoodleId, moduleCmid }: { courseMoodleId?: number; moduleCmid?: number }) {
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [openId, setOpenId] = useState<number | null>(null);
  const [justApproved, setJustApproved] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);

  const pending = usePendingApprovals(courseMoodleId, moduleCmid, offset, PAGE_SIZE);
  const bulkApprove = useBulkApprove();
  const bulkReject = useBulkReject();
  const indexPending = useIndexPending();

  const docs = pending.data?.documents ?? [];
  const total = pending.data?.total;
  const scoped = courseMoodleId !== undefined || moduleCmid !== undefined;
  const scope = { courseMoodleId, moduleCmid };
  const scopeLabel = moduleCmid !== undefined
    ? `in module “${docs[0]?.module_name ?? `cmid ${moduleCmid}`}”`
    : courseMoodleId !== undefined
      ? `in “${docs[0]?.course_fullname ?? `course ${courseMoodleId}`}”`
      : "in every course";
  const busy = bulkApprove.isPending || bulkReject.isPending;

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function decided(approved: number, rejected: number) {
    setJustApproved((n) => n + approved);
    setSelected(new Set());
    setNotice(approved || rejected ? `${approved ? `${approved} approved` : ""}${approved && rejected ? ", " : ""}${rejected ? `${rejected} rejected` : ""}.` : null);
    // A bulk action can empty the current page; step back if so.
    if (offset > 0 && total !== undefined && offset >= total - approved - rejected) setOffset(Math.max(0, offset - PAGE_SIZE));
  }

  const allOnPageSelected = docs.length > 0 && docs.every((d) => selected.has(d.id));
  const n = (count: number) => `${count} pending document${count === 1 ? "" : "s"}`;

  return (
    <>
      <ErrorBanner error={bulkApprove.error || bulkReject.error || indexPending.error} />
      {justApproved > 0 && !indexPending.data && (
        <div className="mb-4 flex items-center justify-between gap-4 rounded border border-green-200 bg-green-50 px-4 py-2 text-sm text-green-800">
          <span>{justApproved} document{justApproved === 1 ? "" : "s"} approved. Index them so the tutor can use them.</span>
          <Button variant="brand" loading={indexPending.isPending} onClick={() => indexPending.mutate()}>Index now</Button>
        </div>
      )}
      {notice && !justApproved && <p className="mb-4 text-sm text-gray-700">{notice}</p>}

      <div className="mb-4 flex flex-wrap justify-end gap-2">
        <Button
          disabled={!selected.size || busy}
          loading={bulkReject.isPending && !!bulkReject.variables?.documentIds}
          onClick={() => {
            if (confirm(`Reject ${selected.size} selected document${selected.size === 1 ? "" : "s"}?`)) {
              bulkReject.mutate({ documentIds: [...selected] }, { onSuccess: (r) => decided(0, r.count) });
            }
          }}
        >
          Reject selected ({selected.size})
        </Button>
        <Button
          variant="brand"
          disabled={!selected.size || busy}
          loading={bulkApprove.isPending && !!bulkApprove.variables?.documentIds}
          onClick={() => bulkApprove.mutate({ documentIds: [...selected] }, { onSuccess: (r) => decided(r.count, 0) })}
        >
          Approve selected ({selected.size})
        </Button>
        <Button
          variant="danger"
          disabled={!total || busy}
          loading={bulkReject.isPending && !bulkReject.variables?.documentIds}
          onClick={() => {
            const question = scoped
              ? `Reject all ${n(total ?? 0)} ${scopeLabel}?`
              : `Reject ALL ${n(total ?? 0)} across every course? This empties the whole review queue.`;
            if (confirm(question)) bulkReject.mutate(scope, { onSuccess: (r) => decided(0, r.count) });
          }}
        >
          {scoped ? "Reject all here" : "Reject all pending"}
        </Button>
        {scoped && (
          <Button
            variant="brand"
            disabled={!total || busy}
            loading={bulkApprove.isPending && !bulkApprove.variables?.documentIds}
            onClick={() => {
              if (confirm(`Approve all ${n(total ?? 0)} ${scopeLabel} without reviewing them individually?`)) {
                bulkApprove.mutate(scope, { onSuccess: (r) => decided(r.count, 0) });
              }
            }}
          >
            {moduleCmid !== undefined ? "Approve whole module" : "Approve whole course"}
          </Button>
        )}
      </div>

      <div className={`grid gap-4 ${openId !== null ? "xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" : ""}`}>
        <Panel>
          <ErrorBanner error={pending.error} />
          {pending.isLoading ? <Loading /> : !docs.length ? (
            <Empty>{offset > 0 ? "No more documents." : "Nothing waiting for approval. Run an extraction after syncing to bring in new content."}</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className={TABLE}>
                <thead>
                  <tr>
                    <th className={`${headCell("first")} w-8`}>
                      <input
                        type="checkbox"
                        aria-label="Select page"
                        checked={allOnPageSelected}
                        onChange={() => setSelected(allOnPageSelected ? new Set() : new Set(docs.map((d) => d.id)))}
                      />
                    </th>
                    <th className={headCell("mid")}>No.</th>
                    <th className={headCell("mid")}>Document</th>
                    <th className={headCell("mid")}>{courseMoodleId === undefined ? "Course / module" : "Module"}</th>
                    <th className={headCell("mid")}>Type</th>
                    <th className={headCell("mid")}>Size</th>
                    <th className={headCell("last")}>Extracted</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d, i) => {
                    const sel = openId === d.id;
                    return (
                      <tr key={d.id} onClick={() => setOpenId(d.id)} className="group cursor-pointer">
                        <td className={`${bodyCell("first", sel)} w-8`}>
                          <input
                            type="checkbox"
                            aria-label={`Select document ${d.id}`}
                            checked={selected.has(d.id)}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggle(d.id)}
                          />
                        </td>
                        <td className={`${bodyCell("mid", sel)} tabular-nums text-gray-500`}>{pad2(offset + i + 1)}</td>
                        <td className={bodyCell("mid", sel)}><DocTitle doc={d} /></td>
                        <td className={bodyCell("mid", sel)}><Origin doc={d} showCourse={courseMoodleId === undefined} /></td>
                        <td className={bodyCell("mid", sel)}><code className="text-xs">{d.source_type}</code></td>
                        <td className={`${bodyCell("mid", sel)} tabular-nums text-gray-600`}>{d.char_count.toLocaleString()} ch</td>
                        <td className={`${bodyCell("last", sel)} text-gray-600`}><RelativeTime iso={d.extracted_at} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!!docs.length && <PageNav offset={offset} limit={PAGE_SIZE} total={total} onChange={(o) => { setOffset(o); setSelected(new Set()); }} />}
        </Panel>

        {openId !== null && (
          <DocumentPanel
            key={openId}
            documentId={openId}
            listed={docs.find((d) => d.id === openId)}
            onClose={() => setOpenId(null)}
            actions={(note, setError) => (
              <PendingActions
                documentId={openId}
                note={note}
                onError={setError}
                onDecided={(approved) => {
                  decided(approved ? 1 : 0, approved ? 0 : 1);
                  // Move to the next document in the list, if any.
                  const idx = docs.findIndex((d) => d.id === openId);
                  setOpenId(docs[idx + 1]?.id ?? null);
                }}
              />
            )}
            footnote="Rejected or approved by mistake? Reopen it from the History tab."
          />
        )}
      </div>
    </>
  );
}

function PendingActions({ documentId, note, onDecided, onError }: {
  documentId: number;
  note: string;
  onDecided: (approved: boolean) => void;
  onError: (error: unknown) => void;
}) {
  const approve = useApprove();
  const reject = useReject();
  const busy = approve.isPending || reject.isPending;
  return (
    <>
      <Button
        variant="danger"
        disabled={busy}
        loading={reject.isPending}
        onClick={() => reject.mutate({ id: documentId, note }, { onSuccess: () => onDecided(false), onError })}
      >
        Reject
      </Button>
      <Button
        variant="brand"
        disabled={busy}
        loading={approve.isPending}
        onClick={() => approve.mutate({ id: documentId, note }, { onSuccess: () => onDecided(true), onError })}
      >
        Approve
      </Button>
    </>
  );
}

// --- history ------------------------------------------------------------------------

function HistoryTab({ courseMoodleId, moduleCmid }: { courseMoodleId?: number; moduleCmid?: number }) {
  const { historyFilter, update } = useFilters();
  const { user } = useAuth();
  const [mine, setMine] = useState(false);
  const [offset, setOffset] = useState(0);
  const [openId, setOpenId] = useState<number | null>(null);
  const [notice, setNotice] = useState<ReactNode>(null);

  const history = useReviewHistory({
    statuses: historyFilter.statuses,
    courseMoodleId,
    moduleCmid,
    reviewedByUserId: mine ? user?.id : undefined,
    offset,
    limit: PAGE_SIZE,
  });
  const docs = history.data?.documents ?? [];
  const canReopen = (d?: ApprovalDocument) => d?.approval_status === "approved" || d?.approval_status === "rejected";

  return (
    <>
      {notice && <div className="mb-4 rounded border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-900">{notice}</div>}
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <select
          aria-label="Decision"
          value={historyFilter.id}
          onChange={(e) => {
            setOffset(0);
            update({ status: e.target.value === HISTORY_FILTERS[0].id ? undefined : e.target.value });
          }}
          className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-brand focus:outline-none"
        >
          {HISTORY_FILTERS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        {user && (
          <label className="flex items-center gap-1.5 text-sm text-gray-700">
            <input type="checkbox" checked={mine} onChange={(e) => { setMine(e.target.checked); setOffset(0); }} />
            Only my decisions
          </label>
        )}
      </div>

      <div className={`grid gap-4 ${openId !== null ? "xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" : ""}`}>
        <Panel>
          <ErrorBanner error={history.error} />
          {history.isLoading ? <Loading /> : !docs.length ? (
            <Empty>No {historyFilter.id === "decided" ? "reviewed" : historyFilter.label.toLowerCase()} documents{courseMoodleId !== undefined ? " in this course" : ""}.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className={TABLE}>
                <thead>
                  <tr>
                    <th className={headCell("first")}>No.</th>
                    <th className={headCell("mid")}>Document</th>
                    <th className={headCell("mid")}>{courseMoodleId === undefined ? "Course / module" : "Module"}</th>
                    <th className={headCell("mid")}>Decision</th>
                    <th className={headCell("last")}>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((d, i) => {
                    const sel = openId === d.id;
                    return (
                      <tr key={d.id} onClick={() => setOpenId(d.id)} className="group cursor-pointer">
                        <td className={`${bodyCell("first", sel)} tabular-nums text-gray-500`}>{pad2(offset + i + 1)}</td>
                        <td className={bodyCell("mid", sel)}><DocTitle doc={d} /></td>
                        <td className={bodyCell("mid", sel)}><Origin doc={d} showCourse={courseMoodleId === undefined} /></td>
                        <td className={bodyCell("mid", sel)}>
                          <StatusBadge status={d.approval_status} label={titleCase(d.approval_status)} />
                          <div className="mt-0.5 text-xs text-gray-500">
                            <RelativeTime iso={d.reviewed_at} />{d.reviewed_by_user_id !== null && ` · user #${d.reviewed_by_user_id}${d.reviewed_by_user_id === user?.id ? " (you)" : ""}`}
                          </div>
                        </td>
                        <td className={`${bodyCell("last", sel)} max-w-xs truncate text-xs text-gray-600`}>{d.review_note ?? <span className="text-gray-400">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {!!docs.length && <PageNav offset={offset} limit={PAGE_SIZE} total={history.data?.total} onChange={setOffset} />}
        </Panel>

        {openId !== null && (
          <DocumentPanel
            key={openId}
            documentId={openId}
            listed={docs.find((d) => d.id === openId)}
            onClose={() => setOpenId(null)}
            noteLabel="Reason for reopening (optional)"
            actions={(note, setError) => canReopen(docs.find((d) => d.id === openId)) && (
              <ReopenAction
                documentId={openId}
                note={note}
                onError={setError}
                onReopened={(doc) => {
                  setOpenId(null);
                  setNotice(<>“{doc.title || `Document #${doc.id}`}” is back in the pending queue{doc.course_moodle_id ? <> for <Link className="underline" to={`/approvals?course=${doc.course_moodle_id}`}>its course</Link></> : null}.</>);
                }}
              />
            )}
            footnote="Reopening moves the document back to pending and removes it from the search index until it's approved again."
          />
        )}
      </div>
    </>
  );
}

function ReopenAction({ documentId, note, onReopened, onError }: {
  documentId: number;
  note: string;
  onReopened: (doc: ApprovalDocument) => void;
  onError: (error: unknown) => void;
}) {
  const reopen = useReopen();
  return (
    <Button
      variant="primary"
      loading={reopen.isPending}
      onClick={() =>
        reopen.mutate({ id: documentId, note }, {
          onSuccess: onReopened,
          onError: (err) =>
            onError(
              err instanceof ApiError && err.status === 409
                ? "This document can't be reopened: it's already pending or has been retired. The list has been refreshed."
                : err
            ),
        })
      }
    >
      Reopen for review
    </Button>
  );
}

// --- shared ---------------------------------------------------------------------------

function DocTitle({ doc }: { doc: ApprovalDocument }) {
  return (
    <>
      <div className="font-medium">{doc.title || <span className="text-gray-400">Untitled</span>}</div>
      <div className="max-w-xs truncate text-xs text-gray-500" title={doc.source_path}>{doc.source_path}</div>
    </>
  );
}

function Origin({ doc, showCourse }: { doc: ApprovalDocument; showCourse: boolean }) {
  return (
    <div className="text-sm text-gray-600">
      {showCourse && (
        <div className="max-w-xs truncate">
          {doc.course_moodle_id !== null ? (
            <Link to={`/courses/${doc.course_moodle_id}`} onClick={(e) => e.stopPropagation()} className="hover:underline">
              {doc.course_fullname ?? `Course ${doc.course_moodle_id}`}
            </Link>
          ) : <span className="text-gray-400">—</span>}
        </div>
      )}
      <div className={`max-w-xs truncate ${showCourse ? "text-xs text-gray-500" : ""}`} title={doc.section_name ?? undefined}>
        {doc.module_name ?? (doc.module_cmid !== null ? `cmid ${doc.module_cmid}` : "—")}
        {doc.section_name && <span className="text-gray-400"> · {doc.section_name}</span>}
      </div>
    </div>
  );
}

function DocumentPanel({ documentId, listed, onClose, actions, footnote, noteLabel = "Review note (optional)" }: {
  documentId: number;
  listed?: ApprovalDocument;
  onClose: () => void;
  actions: (note: string, setError: (error: unknown) => void) => ReactNode;
  footnote?: ReactNode;
  noteLabel?: string;
}) {
  const doc = useDocument(documentId);
  const [note, setNote] = useState("");
  const [actionError, setActionError] = useState<unknown>(null);
  const action = actions(note, setActionError);

  return (
    <Panel
      className="xl:sticky xl:top-4 xl:self-start"
      title={doc.data?.title || listed?.title || `Document #${documentId}`}
      actions={<button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-900" aria-label="Close">✕</button>}
    >
      <ErrorBanner error={doc.error || actionError} />
      {doc.isLoading ? <Loading /> : doc.data && (
        <>
          <dl className="mb-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
            {listed?.course_fullname && (
              <><dt className="text-gray-500">Course</dt><dd>{listed.course_fullname}</dd></>
            )}
            {listed?.module_name && (
              <><dt className="text-gray-500">Module</dt><dd>{listed.module_name}{listed.section_name && <span className="text-gray-500"> · {listed.section_name}</span>}</dd></>
            )}
            <dt className="text-gray-500">Source</dt><dd className="break-all">{doc.data.source_path}</dd>
            <dt className="text-gray-500">Type</dt><dd>{doc.data.source_type}</dd>
            <dt className="text-gray-500">Length</dt><dd>{doc.data.char_count.toLocaleString()} characters</dd>
            {doc.data.media_start_seconds !== null && (
              <><dt className="text-gray-500">Media</dt><dd>{doc.data.media_start_seconds}s – {doc.data.media_end_seconds}s (transcript)</dd></>
            )}
            {listed?.review_note && (
              <><dt className="text-gray-500">Review note</dt><dd>{listed.review_note}</dd></>
            )}
          </dl>
          <div className="max-h-[50vh] overflow-y-auto whitespace-pre-wrap rounded border bg-gray-50 p-3 text-sm leading-relaxed">
            {doc.data.text || <span className="text-gray-400">No text extracted.</span>}
          </div>
          {doc.data.assets.length > 0 && (
            <div className="mt-3 text-xs text-gray-600">
              <div className="mb-1 font-medium">{doc.data.assets.length} attached assets</div>
              <ul className="space-y-0.5">
                {doc.data.assets.map((a) => (
                  <li key={a.id}>{a.kind}: {a.filename} <span className="text-gray-400">({bytes(a.filesize)})</span>{a.alt_text ? ` · “${a.alt_text}”` : ""}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
      {action && (
        <>
          <label htmlFor="review-note" className="mt-4 block text-sm font-medium">{noteLabel}</label>
          <textarea
            id="review-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={4000}
            rows={2}
            className="mt-1 w-full rounded border px-2 py-1 text-sm"
          />
          <div className="mt-3 flex justify-end gap-2">{action}</div>
        </>
      )}
      {footnote && <p className="mt-2 text-xs text-gray-400">{footnote}</p>}
    </Panel>
  );
}
